/**
 * Report formulas: arithmetic over a report's values, such as
 *   [Gross profit] / [Turnover]
 *   ([Revenue] - [Cost of sales]) / [Revenue]
 *   round([Hours] / 7.5, 1)
 *
 * A formula is worked out on each row after the values are added up, so a
 * ratio of two sums is correct per row and on the total row.
 *
 * Shared by the API (which evaluates) and the browser (which checks a
 * formula while it is typed). No dependencies, no eval.
 */

const FUNCTIONS = {
  abs: { min: 1, max: 1, fn: (x) => Math.abs(x) },
  round: { min: 1, max: 2, fn: (x, d = 0) => { const f = 10 ** Math.max(0, Math.min(6, Math.trunc(d))); return Math.round(x * f) / f; } },
  min: { min: 1, max: 20, fn: (...xs) => Math.min(...xs) },
  max: { min: 1, max: 20, fn: (...xs) => Math.max(...xs) },
};

export const FORMULA_FUNCTIONS = Object.keys(FUNCTIONS);

class FormulaError extends Error {
  constructor(message, position) {
    super(message);
    this.position = position;
  }
}

function tokenize(text) {
  const tokens = [];
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (/\s/.test(c)) { i += 1; continue; }
    if (c === '[') {
      const end = text.indexOf(']', i + 1);
      if (end < 0) throw new FormulaError('A field name is missing its closing ].', i);
      const name = text.slice(i + 1, end).trim();
      if (!name) throw new FormulaError('Put a field name between [ and ].', i);
      tokens.push({ type: 'field', value: name, at: i });
      i = end + 1;
      continue;
    }
    const number = /^(\d+(\.\d*)?|\.\d+)/.exec(text.slice(i));
    if (number) {
      tokens.push({ type: 'number', value: Number(number[0]), at: i });
      i += number[0].length;
      continue;
    }
    const word = /^[a-z_][a-z0-9_]*/i.exec(text.slice(i));
    if (word) {
      tokens.push({ type: 'name', value: word[0].toLowerCase(), at: i });
      i += word[0].length;
      continue;
    }
    if ('+-*/(),'.includes(c)) {
      tokens.push({ type: c, at: i });
      i += 1;
      continue;
    }
    throw new FormulaError(`"${c}" cannot be used in a formula.`, i);
  }
  return tokens;
}

/**
 * Parse a formula. Returns { ok: true, ast, fields } where fields are the
 * names in [brackets], or { ok: false, error, position }.
 */
export function parseFormula(text) {
  try {
    const source = String(text || '');
    if (!source.trim()) throw new FormulaError('Enter a formula.', 0);
    const tokens = tokenize(source);
    let pos = 0;
    const peek = () => tokens[pos];
    const take = (type) => {
      const t = tokens[pos];
      if (!t || t.type !== type) {
        const at = t ? t.at : source.length;
        throw new FormulaError(type === ')' ? 'A bracket is not closed.' : 'The formula is incomplete.', at);
      }
      pos += 1;
      return t;
    };

    const primary = () => {
      const t = peek();
      if (!t) throw new FormulaError('The formula is incomplete.', source.length);
      if (t.type === 'number') { pos += 1; return { type: 'number', value: t.value }; }
      if (t.type === 'field') { pos += 1; return { type: 'field', name: t.value }; }
      if (t.type === '(') { pos += 1; const inner = expression(); take(')'); return inner; }
      if (t.type === '-') { pos += 1; return { type: 'negate', arg: primary() }; }
      if (t.type === 'name') {
        const spec = FUNCTIONS[t.value];
        if (!spec) throw new FormulaError(`"${t.value}" is not a function. Use ${FORMULA_FUNCTIONS.join(', ')}, or put field names in [brackets].`, t.at);
        pos += 1;
        take('(');
        const args = [expression()];
        while (peek()?.type === ',') { pos += 1; args.push(expression()); }
        take(')');
        if (args.length < spec.min || args.length > spec.max) throw new FormulaError(`${t.value}() takes ${spec.min === spec.max ? spec.min : `${spec.min} to ${spec.max}`} value${spec.max === 1 ? '' : 's'}.`, t.at);
        return { type: 'call', name: t.value, args };
      }
      throw new FormulaError('Expected a value, a [field] or a bracket here.', t.at);
    };
    const term = () => {
      let node = primary();
      while (peek() && (peek().type === '*' || peek().type === '/')) {
        const op = tokens[pos].type; pos += 1;
        node = { type: 'binary', op, left: node, right: primary() };
      }
      return node;
    };
    function expression() {
      let node = term();
      while (peek() && (peek().type === '+' || peek().type === '-')) {
        const op = tokens[pos].type; pos += 1;
        node = { type: 'binary', op, left: node, right: term() };
      }
      return node;
    }

    const ast = expression();
    if (pos < tokens.length) throw new FormulaError('Something is missing between two parts of the formula.', tokens[pos].at);
    const fields = [...new Set(tokens.filter((t) => t.type === 'field').map((t) => t.value))];
    return { ok: true, ast, fields };
  } catch (err) {
    if (err instanceof FormulaError) return { ok: false, error: err.message, position: err.position };
    throw err;
  }
}

/**
 * Work out a parsed formula. valueOf(name) gives a field's number, or null
 * when it has none; an empty field, or a division by zero, gives null.
 */
export function evaluateFormula(ast, valueOf) {
  const run = (node) => {
    switch (node.type) {
      case 'number': return node.value;
      case 'field': {
        const v = valueOf(node.name);
        if (v === null || v === undefined || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      }
      case 'negate': { const v = run(node.arg); return v === null ? null : -v; }
      case 'call': {
        const args = node.args.map(run);
        if (args.some((a) => a === null)) return null;
        return FUNCTIONS[node.name].fn(...args);
      }
      case 'binary': {
        const a = run(node.left);
        const b = run(node.right);
        if (a === null || b === null) return null;
        if (node.op === '+') return a + b;
        if (node.op === '-') return a - b;
        if (node.op === '*') return a * b;
        return b === 0 ? null : a / b;
      }
      default: return null;
    }
  };
  const out = run(ast);
  return out === null || !Number.isFinite(out) ? null : out;
}
